import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Papa from 'papaparse';
import AWS from 'aws-sdk';
import './Globe.css';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';
import { SpinnerRoundOutlined } from 'spinners-react';
import ReactGlobe from 'react-globe';
import ReactModal from 'react-modal';
import { useNavigate } from 'react-router-dom';

const shipTypeColors = {
  Cargo: 'red',
  Tanker: 'green',
  Fishing: 'orange',
  'Special craft': 'blue',
  Passenger: 'yellow',
  Other: 'white',
};

function Legend({ shipTypeColors, onNavigate }) {
  const legendItems = Object.entries(shipTypeColors).map(([type, color]) => (
    <div key={type} className="legend-item">
      <div className="legend-color" style={{ backgroundColor: color }}></div>
      <div className="legend-label">{type}</div>
    </div>
  ));

  return (
    <div className="legend">
      <div className="button-container">
        <button onClick={onNavigate}>Map View</button>
      </div>
      <h3>Legend</h3>
      <div className="legend-items">{legendItems}</div>
    </div>
  );
}

function Globe() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allMarkers, setAllMarkers] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [shipNames, setShipNames] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({});
  const [selectedShip, setSelectedShip] = useState(null);
  const [shipRotation, setShipRotation] = useState(0);
  const [selectedShipCoordinates, setSelectedShipCoordinates] = useState(null);
  const [cameraOptions, setCameraOptions] = useState({
    enableCameraZoom: true,
    autoRotate: false,
    autoRotateSpeed: 0.5,
    focusAnimationDuration: 2000,
    focusEasingFunction: ['Linear', 'None'],
    zoomEasingFunction: ['Quadratic', 'InOut'],
  });
  const [cameraState, setCameraState] = useState({
    position: [0, 0],
    zoom: 1,
  });
  const [shipImageUrls, setShipImageUrls] = useState({});
  const navigateToMapData = () => {
    navigate('/mapdata'); // Change to your desired path
  };

  useEffect(() => {
    // Update ship rotation every 100 milliseconds
    const intervalId = setInterval(() => {
      setShipRotation(rotation => rotation + 1);
    }, 175);

    // Clear interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    async function fetchData() {
      try { 
        AWS.config.update({
          accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
          secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
          region: process.env.REACT_APP_AWS_REGION,
        });
        
        const s3 = new AWS.S3();
        const params = {
          Bucket: "git-dash-bucket",
          Key: "vcss_map_data.csv",
        };

        const data = await s3.getObject(params).promise();
        const csvData = data.Body.toString("utf-8");

        // Parse CSV data
        const shipData = {};
        
        Papa.parse(csvData, {
          header: true,
          complete: (result) => {
            result.data.forEach((row) => {
              const shipName = row.SHIPNAME;

              if (
                row.LAT &&
                row.LON &&
                shipName &&
                row.TIMESTAMP &&
                row.AIS_TYPE_SUMMARY &&
                row.IMO
              ) {
                const imo = row.IMO; 
                if (
                  !shipData[shipName] ||
                  row.TIMESTAMP > shipData[shipName].TIMESTAMP
                ) {
                  let color;
                  switch (row.AIS_TYPE_SUMMARY.toLowerCase()) {
                    case "cargo":
                      color = "red";
                      break;
                    case "tanker":
                      color = "green";
                      break;
                    case "fishing":
                      color = "orange";
                      break;
                    case "special craft":
                      color = "blue";
                      break;
                    case "passenger":
                      color = "yellow";
                      break;
                    default:
                      color = "white"; // Default color for unknown types
                  }

                  shipData[shipName] = {
                    id: Object.keys(shipData).length + 1,
                    name: shipName,
                    color,
                    coordinates: [parseFloat(row.LAT), parseFloat(row.LON)],
                    value: 50,
                    timestamp: row.TIMESTAMP,
                    temperature: parseFloat(row.TEMPERATURE), // Parse temperature as a number
                    salinity: parseFloat(row.SALINITY), // Add salinity
                    lastPort: row.LAST_PORT,
                    type: row.AIS_TYPE_SUMMARY,
                    imo: row.imo,
                    imageUrl: '' // Placeholder for ship image URL
                  };
                }
              }
            });

            const parsedMarkers = Object.values(shipData);
            setAllMarkers(parsedMarkers);
            setMarkers(parsedMarkers);
            setShipNames(Object.keys(shipData).sort());
            setLoading(false);
          },
        });
   
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const fetchShipImage = async (imo) => {
    try {
      console.log('Fetching ship image for IMO:', imo); // Debugging statement
      const response = await fetch(`https://backend-nu-two.vercel.app/getShipImage/${imo}`);
      if (response.ok) {
        const shipImageBlob = await response.blob();
        const imageUrl = URL.createObjectURL(shipImageBlob);
        console.log('Ship image URL:', imageUrl); // Debugging statement
        return imageUrl;
      } else {
        console.error('Error fetching ship image');
        return null;
      }
    } catch (error) {
      console.error('Error fetching ship image:', error);
      return null;
    }
  };
  

  const handleShipNameClick = async (shipName) => {
    const selectedMarker = allMarkers.find((marker) => marker.name === shipName);
    console.log('Selected Marker:', selectedMarker); // Debugging statement
    if (selectedMarker) {
      setSelectedShip(shipName);
      setSelectedShipCoordinates(selectedMarker.coordinates);
      setMarkers([selectedMarker]);
      setCameraState({
        position: selectedMarker.coordinates,
        zoom: 5, // Adjust zoom level as needed
      });
  
      try {
        const response = await fetch(`https://backend-nu-two.vercel.app/getShipImage/${selectedMarker.imo}`);
        if (response.ok) {
          const shipImageBlob = await response.blob();
          const imageUrl = URL.createObjectURL(shipImageBlob);
          console.log('Ship image URL:', imageUrl); // Debugging statement
          setModalContent({
            shipName: selectedMarker.name,
            latitude: selectedMarker.coordinates[0],
            longitude: selectedMarker.coordinates[1],
            temperature: selectedMarker.temperature,
            salinity: selectedMarker.salinity,
            imo: selectedMarker.imo,
            lastPort: selectedMarker.lastPort,
            imageUrl: imageUrl // Set ship image URL
          });
          setIsModalOpen(true); // Open the modal
        } else {
          console.error('Error fetching ship image');
          // Handle error case here
        }
      } catch (error) {
        console.error('Error fetching ship image:', error);
        // Handle error case here
      }
    }
    else {
      console.log('Image URL not found for marker:', selectedMarker); // Debugging statement
    }
  };
  
  const handleRightClick = (event) => {
    event.preventDefault();
    setSelectedShip(null);
    setSelectedShipCoordinates(null);
    setMarkers(allMarkers);
    setCameraOptions((prevOptions) => ({
      ...prevOptions,
      focus: null,
    }));
  };
  
  return (
    <div className="app-container" onContextMenu={handleRightClick}>
      {loading && <SpinnerRoundOutlined />}
      <div className="sidebar">
        <div className="sidebar-header">
          <h2>GIT Fleet</h2>
        </div>
       
        <ul className="sidebar-list">
          {shipNames.map((shipName, index) => (
            <li
              key={index}
              className={shipName === selectedShip ? "selected" : ""}
              onClick={() => handleShipNameClick(shipName, allMarkers.find((marker) => marker.name === shipName)?.imo)}
            >
              {shipName}
            </li>
          ))}
        </ul>
      </div>
      <div className="globe-container">
        <div className="react-globe-container">
          <ReactGlobe
            height="100vh"
            markers={markers}
            cameraOptions={cameraOptions}
            atmosphereColor="rgba(255, 0, 255, 0.9)" // Customize atmosphere color
            atmosphereAltitude="0.33" // Adjust atmosphere altitude
            atmosphereThickness="0.65"
            enableGlobeGlow={true} // Enable globe glow effect
            globeGlowColor="rgba(255, 255, 0, 0.6)" // Customize globe glow color
            globeGlowCoefficient="0.65" // Adjust globe glow coefficient
            globeGlowPower="4.3" // Increase globe glow power
            cameraState={
              selectedShipCoordinates
                ? { position: selectedShipCoordinates, zoom: 5 }
                : cameraState
            }
            onClickMarker={(marker) => {
              console.log('Clicked Marker:', marker); // Debugging statement
              
                console.log('Image URL:', marker.imageUrl); // Debugging statement
                setModalContent({
                  shipName: marker.name,
                  latitude: marker.coordinates[0],
                  longitude: marker.coordinates[1],
                  temperature: marker.temperature,
                  salinity: marker.salinity,
                  lastPort: marker.lastPort,
                  imageUrl: marker.imageUrl,
                  imo: marker.imo
                });
                setIsModalOpen(true);
              
            }}
            onDefocus={(previousFocus) => {
              console.log("Defocused:", previousFocus);
            }}
            onGlobeTextureLoaded={() => setLoading(false)}
            enableBackgroundStars={true} // Enable background stars
            enableBackgroundStarsHalo={true} // Add a halo effect to background stars
            backgroundStarsHaloColor="rgba(255, 255, 255, 0.8)" // Increase the halo effect intensity
            enableCameraAutoRotate={true} // Enable auto-rotation
            cameraAutoRotateSpeed="2.5" // Increase auto-rotation speed for a livelier effect
            enableMarkerGlow={true} // Enable marker glow
            markerGlowColor="rgba(255, 0, 0, 0.8)" // Increase marker glow intensity and visibility
            markerGlowCoefficient="0.88" // Adjust marker glow coefficient
            markerGlowPower="5" // Increase marker glow power
            enableMarkerTooltip={true}
            markerTooltipRenderer={(marker) => (
              <div>
                <h3>{marker.name}</h3>
                <p>Latitude: {marker.coordinates[0]}</p>
                <p>Longitude: {marker.coordinates[1]}</p>
              </div>
            )}
          />
        </div>
        <div className="ship" style={{ transform: `rotate(${shipRotation}deg) translateY(-50%) rotate(135deg)` }}>
          {/* Add ship image here */}
          <img src="/ship.png" alt="Ship" style={{ width: '50px', height: 'auto' }} />
        </div>
        {loading && <p>Loading data...</p>}
      </div>
      <ReactModal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        ariaHideApp={false}
        className="modal"
      >
        <h3>Ship Information</h3>
        <h5>"Have some coffee before seeing all of this" - <i>Your Mind</i></h5>
        <img src={modalContent.imageUrl} alt="Ship" style={{ width: '100%', height: 'auto' }} />
        <p>Ship Name: {modalContent.shipName}</p>
        <p>Latitude: {modalContent.latitude}</p>
        <p>Longitude: {modalContent.longitude}</p>
        {typeof modalContent.temperature === 'number' && (
          <p>Temperature: {modalContent.temperature.toFixed(2)}</p>
        )}
        {typeof modalContent.salinity === 'number' && (
          <p>Salinity: {modalContent.salinity.toFixed(2)}</p>
        )}
        <p>Last Port: {modalContent.lastPort}</p>
        
        <button onClick={() => setIsModalOpen(false)}>Close</button>
      </ReactModal>
      <Legend shipTypeColors={shipTypeColors} onNavigate={navigateToMapData} />
    </div>
  );
}

export default Globe;
